<script setup lang="ts">
  import { onMounted, onUnmounted, watch } from 'vue'
  import {
    type CaseCreateEntityWithRelationshipsRequest,
    type CustomerCase as Case,
    type CustomerEntity,
    EntityTypeEnum,
    RelationshipTypeEnum,
    StateEnum,
    CasesService
  } from '@/types/generated'
  import Entities from '@/components/app/cases/entities/Entities.vue'
  import { useCaseStore } from '@/store/app/case/detail/caseDetailStore'

  const emit = defineEmits(['update:entities', 'invalid'])

  const caseStore = useCaseStore()

  const createEmptyEntity = (entityType: EntityTypeEnum): CustomerEntity => ({
    uuid: 'temp-' + crypto.randomUUID(),
    entity_type: entityType,
    title: '',
    specific_title: '',
    first_name: '',
    last_name: '',
    company_name: '',
    company_legal_form: '',
    institution_name: '',
    phone: '',
    email: '',
    date_of_birth: null,
    street: '',
    street_number: '',
    address_line_2: '',
    zip: '',
    city: '',
    legal_insurance: null,
    legal_insurance_number: '',
    legal_insurance_name: '',
    is_company: entityType === EntityTypeEnum.LEGAL_PERSON ? 'True' : 'False',
    changes: [],
    proposed_changes: []
  })

  const clientEntity = createEmptyEntity(EntityTypeEnum.NATURAL_PERSON)

  const mockCase = {
    id: 0,
    uuid: 'temp-case-' + crypto.randomUUID(),
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    modified_at: new Date().toISOString(),
    jupus_case_number: '',
    inquiry: '',
    note: '',
    type: 'MANUAL',
    questionnaire_submissions: [],
    form_requests: [],
    pdf_form_submissions: [],
    virtual_documents: [],
    clerk: null,
    services: [],
    state: StateEnum.NEW,
    chat_sessions: [],
    activities: [],
    case_lifecycle_state: null,
    source_call_uuid: null,
    source_inbound_email_uuid: null,
    has_pending_duplicate_matches: false,
    case_title: '',
    org: {} as any,
    data: {
      uuid: 'temp-data-' + crypto.randomUUID(),
      client: clientEntity,
      opponent: null,
      law: {
        field: '',
        topic: ''
      },
      entities: [
        {
          uuid: 'temp-client-rel-' + crypto.randomUUID(),
          entity: clientEntity,
          relationships: [
            {
              uuid: 'temp-client-rel-inner-' + crypto.randomUUID(),
              relationship_type: RelationshipTypeEnum.ENTITIES_CLIENT,
              is_unique_per_case: true,
              number: null
            }
          ]
        }
      ],
      changes: [],
      mappers: []
    }
  } as unknown as Case

  onMounted(async () => {
    caseStore.isCreatingCase = true
    caseStore.caseRecord = mockCase

    try {
      const mappersResponse = await CasesService.casesGenericDatamappersList()
      if (mockCase.data && mappersResponse.results && caseStore.caseRecord) {
        caseStore.caseRecord = {
          ...mockCase,
          data: {
            ...mockCase.data,
            mappers: mappersResponse.results
          }
        } as Case
      }
    } catch (error) {
      console.error('Failed to load mappers:', error)
    }
  })

  watch(
    () => caseStore.caseRecord?.data?.entities,
    (entities) => {
      if (!entities) return

      const entitiesData: CaseCreateEntityWithRelationshipsRequest[] = entities.map((e) => {
        const sourceDocumentUuid = caseStore.entitySourceDocumentMap.get(e.entity.uuid)

        const buildAiAugmentedFields = () => {
          if (!sourceDocumentUuid || !e.entity.changes) return null

          const aiAugmentedFields: Record<string, string | null> = {}

          // Populate from AI-sourced changes
          e.entity.changes.forEach((change) => {
            if (change.change_source === 'AI' && change.ai_augmentation?.document) {
              const fieldName = change.entities_mapper_key.replace('entities.field.', '')
              aiAugmentedFields[fieldName] = change.ai_augmentation.document.uuid
            }
          })

          return Object.keys(aiAugmentedFields).length > 0 ? aiAugmentedFields : null
        }

        return {
          entity: {
            entity_type: e.entity.entity_type,
            title: e.entity.title || '',
            specific_title: e.entity.specific_title || '',
            first_name: e.entity.first_name || '',
            last_name: e.entity.last_name || '',
            company_name: e.entity.company_name || '',
            company_legal_form: e.entity.company_legal_form || '',
            institution_name: e.entity.institution_name || '',
            phone: e.entity.phone || '',
            email: e.entity.email || '',
            date_of_birth: e.entity.date_of_birth || null,
            street: e.entity.street || '',
            street_number: e.entity.street_number || '',
            address_line_2: e.entity.address_line_2 || '',
            zip: e.entity.zip || '',
            city: e.entity.city || '',
            legal_insurance: e.entity.legal_insurance || null,
            legal_insurance_number: e.entity.legal_insurance_number || '',
            legal_insurance_name: e.entity.legal_insurance_name || ''
          },
          relationships: e.relationships.map((r) => ({
            relationship_type: r.relationship_type
          })),
          ai_augmented_fields: buildAiAugmentedFields()
        }
      })
      emit('update:entities', entitiesData)

      const hasClient = entitiesData.some((e) =>
        e.relationships.some((r) => r.relationship_type === RelationshipTypeEnum.ENTITIES_CLIENT)
      )
      emit('invalid', !hasClient)
    },
    { deep: true, immediate: true }
  )

  onUnmounted(() => {
    caseStore.resetCreationState()
  })
</script>

<template>
  <div class="mb-8">
    <Entities :case-create-mode="true" />
  </div>
</template>
